import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ContentStateService } from '../../../content-state.service';
import { ChunkService } from '../../chunk.service';

const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

@Component({
  selector: 'app-file-chunk-displayer',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule],
  styleUrls: ['./media-chunk-displayer.scss'],
  template: `
    @if (chunk(); as chunk) {
      <div class="media-card" [class.clickable]="canPreview()" (click)="canPreview() && viewDocument(chunk.mainEdition.content)">
        <mat-icon class="media-icon" [matTooltip]="fileType()" matTooltipPosition="above">{{ fileIcon() }}</mat-icon>

        <div class="media-content">
          <span class="media-name" [matTooltip]="originalFilename()" matTooltipPosition="above">
            {{ originalFilename() }}
          </span>
        </div>

        <div class="media-actions">
          <button
            mat-icon-button
            class="media-action-button"
            (click)="$event.stopPropagation(); downloadFile(chunk.mainEdition.content, chunk.mainEdition.filename)"
            [matTooltip]="'FileChunkDisplayer.Download' | translate"
          >
            <mat-icon>download</mat-icon>
          </button>
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileChunkDisplayerComponent {
  chunk = input.required<any>();

  private chunkService = inject(ChunkService);
  private contentStateService = inject(ContentStateService);

  pdfAvailable = signal(false);

  constructor() {
    effect(() => {
      const c = this.chunk();
      if (!c) return;
      const filename = c.mainEdition?.filename || '';
      const extension = filename.split('.').pop()?.toLowerCase() || '';
      if (!OFFICE_EXTENSIONS.includes(extension)) return;
      const fileLink = c.mainEdition?.content;
      if (!fileLink) return;
      const pdfFileLink = fileLink.replace(new RegExp(`\\.${extension}$`, 'i'), '.pdf');
      this.chunkService.getDownloadURL(pdfFileLink).then((url) => {
        this.pdfAvailable.set(!!url && url !== 'images/cancel.png');
      });
    });
  }

  fileType = computed(() => {
    const filename = this.chunk()?.mainEdition?.filename || '';
    const extension = filename.split('.').pop()?.toLowerCase() || 'file';
    return `${extension.toUpperCase()}`;
  });

  fileIcon = computed(() => {
    const filename = this.chunk()?.mainEdition?.filename || '';
    const extension = filename.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'doc':
      case 'docx':
        return 'odt';
      case 'xls':
      case 'xlsx':
        return 'table_chart';
      case 'zip':
      case 'rar':
        return 'archive';
      default:
        return 'insert_drive_file';
    }
  });

  canPreview = computed(() => {
    const filename = this.chunk()?.mainEdition?.filename || '';
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    if (OFFICE_EXTENSIONS.includes(extension)) {
      return this.pdfAvailable();
    }
    const supportedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    return supportedExtensions.includes(extension);
  });

  originalFilename = computed(() => this.chunk()?.mainEdition?.filename || '');

  viewDocument(fileLink: string): void {
    const filename = this.chunk().mainEdition.filename;
    let extension = filename.split('.').pop()?.toLowerCase() || '';

    // If PDF version was found to be available, use it directly
    if (this.pdfAvailable()) {
      const pdfFileLink = fileLink.replace(new RegExp(`\\.${extension}$`, 'i'), '.pdf');
      const pdfFilename = filename.replace(new RegExp(`\\.${extension}$`, 'i'), '.pdf');
      this.chunkService.getDownloadURL(pdfFileLink).then((pdfUrl) => {
        if (pdfUrl && pdfUrl !== 'images/cancel.png') {
          this.contentStateService.openFileViewer({
            fileLink: pdfUrl,
            fileName: pdfFilename,
            fileType: 'pdf',
          });
          return;
        }
        this.pdfAvailable.set(false);
        this.openOriginal(fileLink, filename, extension);
      });
      return;
    }

    this.openOriginal(fileLink, filename, extension);
  }

  private openOriginal(fileLink: string, filename: string, extension: string): void {
    this.chunkService.getDownloadURL(fileLink).then((url) => {
      const commonExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
      let ext = extension;
      if (!commonExtensions.includes(ext)) {
        try {
          const urlPath = decodeURIComponent(url.split('?')[0]);
          const urlExt = urlPath.split('.').pop()?.toLowerCase();
          if (urlExt && commonExtensions.includes(urlExt)) {
            ext = urlExt;
          }
        } catch (e) {
          console.warn('Failed to extract extension from URL', e);
        }
      }

      // Fallback: if it's an Office file, try the PDF version as a last resort
      if (OFFICE_EXTENSIONS.includes(ext)) {
        const pdfFileLink = fileLink.replace(new RegExp(`\\.${ext}$`, 'i'), '.pdf');
        this.chunkService.getDownloadURL(pdfFileLink).then((pdfUrl) => {
          if (pdfUrl && pdfUrl !== 'images/cancel.png') {
            this.pdfAvailable.set(true);
            this.contentStateService.openFileViewer({
              fileLink: pdfUrl,
              fileName: filename.replace(new RegExp(`\\.${ext}$`, 'i'), '.pdf'),
              fileType: 'pdf',
            });
          } else {
            this.contentStateService.openFileViewer({
              fileLink: url,
              fileName: filename,
              fileType: ext,
            });
          }
        });
        return;
      }

      this.contentStateService.openFileViewer({
        fileLink: url,
        fileName: filename,
        fileType: ext,
      });
    });
  }

  downloadFile(fileLink: string, name: string): void {
    this.chunkService.getDownloadURL(fileLink).then((url) => {
      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const blobURL = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobURL;
          if (name?.length) {
            a.download = name;
          }
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        });
    });
  }
}
